/**
 * Pre-styled Chip component with glass morphism effect.
 * Eliminates repetitive sx props for chip styling across the application.
 */

import React from 'react';
import { Chip, ChipProps } from '@mui/material';
import { BG_COLORS, BORDER_COLORS, WIKI_COLORS, USER_ROLE_COLORS } from '../../utils/colors';

// ============================================
// GLASS CHIP
// ============================================

interface GlassChipProps extends Omit<ChipProps, 'variant'> {
  /**
   * Visual variant of the chip
   */
  glassVariant?: 'default' | 'outlined' | 'success' | 'error' | 'warning';
}

const VARIANT_STYLES = {
  default: {
    background: BG_COLORS.GLASS_LIGHT,
    border: `1px solid ${BORDER_COLORS.STRONG}`,
    color: 'white',
  },
  outlined: {
    background: 'transparent',
    border: `1px solid ${BORDER_COLORS.STRONG}`,
    color: 'white',
  },
  success: {
    background: BG_COLORS.SUCCESS_LIGHT,
    border: `1px solid ${BORDER_COLORS.SUCCESS}`,
    color: '#86efac',
  },
  error: {
    background: BG_COLORS.ERROR_LIGHT,
    border: `1px solid ${BORDER_COLORS.ERROR}`,
    color: '#fca5a5',
  },
  warning: {
    background: BG_COLORS.WARNING_LIGHT,
    border: `1px solid ${BORDER_COLORS.WARNING}`,
    color: '#fcd34d',
  },
};

/**
 * Pre-styled glass Chip.
 * Replaces repetitive pattern:
 * sx={{ background: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(255, 255, 255, 0.2)', color: 'white', fontWeight: 600 }}
 *
 * @example
 * <GlassChip label="Status" />
 * <GlassChip label="Active" glassVariant="success" />
 * <GlassChip label="Error" glassVariant="error" />
 */
const GlassChip: React.FC<GlassChipProps> = ({
  glassVariant = 'default',
  sx,
  ...props
}) => {
  const variantStyles = VARIANT_STYLES[glassVariant] || VARIANT_STYLES.default;

  return (
    <Chip
      {...props}
      sx={{
        ...variantStyles,
        fontWeight: 600,
        '& .MuiChip-icon': { color: variantStyles.color },
        ...sx,
      }}
    />
  );
};

export default GlassChip;

// ============================================
// RANK CHIP (for tables/ratings)
// ============================================

interface RankChipProps extends ChipProps {
  /**
   * Rank number to display
   */
  rank: number;
}

/**
 * Chip styled for displaying ranks in tables.
 */
export const RankChip: React.FC<RankChipProps> = ({
  rank,
  sx,
  ...props
}) => {
  return (
    <Chip
      {...props}
      label={rank}
      size="small"
      sx={{
        fontWeight: 'bold',
        minWidth: 36,
        height: 28,
        background: BG_COLORS.GLASS_LIGHT,
        border: `1px solid ${BORDER_COLORS.STRONG}`,
        color: 'white',
        '& .MuiChip-label': {
          px: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
        ...sx,
      }}
    />
  );
};

// ============================================
// ROLE CHIP
// ============================================

interface RoleChipProps extends Omit<ChipProps, 'color'> {
  /** Роль пользователя: задаёт цвет чипа */
  role: string;
  /** Готовая подпись (перевод делает вызывающая сторона) */
  label: React.ReactNode;
}

/**
 * Чип роли пользователя — один вид на всё приложение: подложка и рамка в цвете роли,
 * сам текст тоже цветной. Так же выглядят чипы типа предложения в магазине и лимита покупок.
 *
 * @example <RoleChip role={user.role} label={t(`admin:users.roles.${user.role.toLowerCase()}`)} />
 */
export const RoleChip: React.FC<RoleChipProps> = ({ role, sx, ...props }) => {
  const color = USER_ROLE_COLORS[role] || USER_ROLE_COLORS.VERIFIED_USER;

  return (
    <Chip
      {...props}
      size="small"
      sx={{
        background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)`,
        color,
        border: `1px solid ${color}40`,
        fontWeight: 600,
        height: { xs: 20, sm: 24 },
        '& .MuiChip-label': { px: { xs: 0.75, sm: 1 }, fontSize: { xs: '0.6rem', sm: '0.75rem' } },
        ...sx,
      }}
    />
  );
};

// ============================================
// STATUS CHIP
// ============================================

type StatusType = 'active' | 'inactive' | 'pending' | 'success' | 'error' | 'warning' | 'info';

interface StatusChipProps extends Omit<ChipProps, 'color'> {
  /**
   * Status type that determines the chip appearance
   */
  status: StatusType;
}

const STATUS_STYLES: Record<StatusType, { background: string; color: string }> = {
  active: {
    background: WIKI_COLORS.GREEN.background,
    color: WIKI_COLORS.GREEN.text,
  },
  success: {
    background: WIKI_COLORS.GREEN.background,
    color: WIKI_COLORS.GREEN.text,
  },
  inactive: {
    background: WIKI_COLORS.GRAY.background,
    color: WIKI_COLORS.GRAY.text,
  },
  pending: {
    background: WIKI_COLORS.AMBER.background,
    color: WIKI_COLORS.AMBER.text,
  },
  warning: {
    background: WIKI_COLORS.AMBER.background,
    color: WIKI_COLORS.AMBER.text,
  },
  error: {
    background: WIKI_COLORS.RED.background,
    color: WIKI_COLORS.RED.text,
  },
  info: {
    background: WIKI_COLORS.BLUE.background,
    color: WIKI_COLORS.BLUE.text,
  },
};

/**
 * Chip for displaying status values with appropriate styling.
 *
 * @example
 * <StatusChip status="active" label="Active" />
 * <StatusChip status="pending" label="Pending" />
 */
export const StatusChip: React.FC<StatusChipProps> = ({
  status,
  sx,
  ...props
}) => {
  const styles = STATUS_STYLES[status] || STATUS_STYLES.inactive;

  return (
    <Chip
      {...props}
      size="small"
      sx={{
        background: styles.background,
        color: styles.color,
        fontWeight: 500,
        ...sx,
      }}
    />
  );
};
